<script setup>
import { ref, onMounted, onBeforeUnmount, inject, computed } from "vue";
import FrontendLogo from "@/Components/FrontendLogo.vue";
import { Link, usePage } from "@inertiajs/vue3";
import { tWithFallback as twf } from "@/i18n/helper";
import LanguageSwitcher from "@/Components/LanguageSwitcher.vue";
import { HugeiconsIcon } from "@hugeicons/vue";
import {
  Home01Icon,
  InformationCircleIcon,
  UserMultipleIcon,
  MortarboardIcon,
  FolderOpenIcon,
  News01Icon,
  Image02Icon,
  Menu01Icon,
  Cancel01Icon,
  Mail01Icon,
  Facebook01Icon,
  InstagramIcon,
  YoutubeIcon,
} from "@hugeicons/core-free-icons";

const route = inject("route");

defineProps({
  menu: {
    type: Array,
    default: () => [],
  },
});

const page = usePage();
const tWithFallback = twf;

// Menu dinamis dari admin (dibagikan via HandleInertiaRequests).
const dynamicMenus = computed(() => page.props?.frontendMenus || []);

const stickyMenu = ref(false);
const isMobileMenuVisible = ref(false);

const toggleMobileMenu = () => {
  isMobileMenuVisible.value = !isMobileMenuVisible.value;
};
const closeMobileMenu = () => {
  isMobileMenuVisible.value = false;
};

// Item menu (datar, ikon di atas label). Label tetap lewat i18n.
const navItems = [
  { key: "menu.home", fallback: "Beranda", route: "home", icon: Home01Icon },
  { key: "menu.about", fallback: "Profil", route: "profil", icon: InformationCircleIcon },
  // { key: "menu.leaders", fallback: "Pimpinan", route: "pejabat", icon: UserMultipleIcon },
  { key: "menu.lecturers", fallback: "Dosen", route: "dosenjurusan", icon: MortarboardIcon },
  { key: "menu.documents", fallback: "Dokumen", route: "dokumen", icon: FolderOpenIcon },
  { key: "menu.information", fallback: "Informasi", route: "informasi", icon: News01Icon },
  { key: "menu.gallery", fallback: "Galeri", route: "galeri", icon: Image02Icon },
];

const isActive = (name) => {
  try {
    return route().current(name);
  } catch (e) {
    return false;
  }
};

const handleScroll = () => {
  stickyMenu.value = window.scrollY > 0;
};
const handleClickOutside = (event) => {
  if (
    !event.target.closest(".mobile-menu") &&
    !event.target.closest(".toggle-button")
  ) {
    closeMobileMenu();
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  window.addEventListener("scroll", handleScroll);
});
onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
  window.removeEventListener("scroll", handleScroll);
});
</script>

<template>
  <div>
    <header class="fixed top-0 left-0 w-full z-[99999]">
      <!-- ===== Top bar ===== -->
      <div class="relative z-20 text-white bg-primary/90 backdrop-blur-lg supports-[backdrop-filter]:bg-primary/80">
        <div class="max-w-[1170px] mx-auto px-4 sm:px-8 xl:px-0 h-9 flex items-center justify-between text-xs">
          <div class="flex items-center gap-4">
            <a v-if="page.props.apps?.linkemail" :href="`mailto:${page.props.apps.linkemail}`"
              class="inline-flex items-center gap-1.5 transition-opacity hover:opacity-80">
              <HugeiconsIcon :icon="Mail01Icon" :size="14" :stroke-width="1.8" />
              <span class="hidden sm:inline">{{ page.props.apps.linkemail }}</span>
            </a>
          </div>

          <div class="flex items-center gap-3">
            <div class="items-center hidden gap-3 sm:flex">
              <a v-if="page.props.apps?.linkfb" :href="page.props.apps.linkfb" target="_blank" rel="noopener noreferrer"
                class="transition-opacity hover:opacity-80" title="Facebook">
                <HugeiconsIcon :icon="Facebook01Icon" :size="16" :stroke-width="1.8" />
              </a>
              <a v-if="page.props.apps?.linkinstagram" :href="page.props.apps.linkinstagram" target="_blank"
                rel="noopener noreferrer" class="transition-opacity hover:opacity-80" title="Instagram">
                <HugeiconsIcon :icon="InstagramIcon" :size="16" :stroke-width="1.8" />
              </a>
              <a v-if="page.props.apps?.linkyoutube" :href="page.props.apps.linkyoutube" target="_blank"
                rel="noopener noreferrer" class="transition-opacity hover:opacity-80" title="YouTube">
                <HugeiconsIcon :icon="YoutubeIcon" :size="16" :stroke-width="1.8" />
              </a>
              <span class="w-px h-4 bg-white/30"></span>
            </div>
            <LanguageSwitcher />
          </div>
        </div>
      </div>

      <!-- ===== Main bar ===== -->
      <div class="transition-shadow duration-300 border-b bg-white/70 backdrop-blur-lg border-white/40 supports-[backdrop-filter]:bg-white/60"
        :class="{ 'shadow-sm': stickyMenu }">
        <div class="max-w-[1170px] mx-auto px-4 sm:px-8 xl:px-0 h-16 grid grid-cols-[1fr_auto_1fr] items-center gap-4">
          <!-- Logo (kiri) -->
          <Link :href="route('home')" class="flex items-center gap-3 w-fit">
            <FrontendLogo class="w-10 h-10 text-primary" />
            <span class="text-sm font-bold sm:text-base text-maroon-800 font-roboto-slab whitespace-nowrap">
              {{ page.props.apps?.namaapps }}
            </span>
          </Link>

          <!-- Desktop menu: di tengah, gaya pill modern -->
          <nav class="items-center hidden gap-1 lg:flex justify-self-center">
            <Link v-for="item in navItems" :key="item.route" :href="route(item.route)"
              class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors rounded-full font-roboto-slab"
              :class="isActive(item.route)
                ? 'bg-primary/10 text-primary'
                : 'text-gray-600 hover:text-primary hover:bg-primary/5'
                ">
              <HugeiconsIcon :icon="item.icon" :size="18" :stroke-width="1.8" />
              <span class="whitespace-nowrap">
                {{ tWithFallback(item.key, item.fallback) }}
              </span>
            </Link>
            <!-- Menu dinamis dari admin -->
            <Link v-for="m in dynamicMenus" :key="m.slug" :href="`/halaman/${m.slug}`"
              class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors rounded-full font-roboto-slab text-gray-600 hover:text-primary hover:bg-primary/5">
              <i v-if="m.icon" :class="m.icon" class="text-base"></i>
              <span class="whitespace-nowrap">{{ m.label }}</span>
            </Link>
          </nav>

          <!-- Aksi kanan / hamburger -->
          <div class="flex items-center justify-end">
            <button aria-label="Buka menu" class="block text-gray-700 lg:hidden toggle-button"
              @click="toggleMobileMenu">
              <HugeiconsIcon :icon="Menu01Icon" :size="24" :stroke-width="1.8" />
            </button>
          </div>
        </div>
      </div>
    </header>

    <!-- ===== Mobile drawer ===== -->
    <Teleport to="body">
      <div v-if="isMobileMenuVisible" class="mobile-menu fixed inset-0 bg-black bg-opacity-40 z-[100000] lg:hidden"
        @click.self="closeMobileMenu">
        <div class="relative w-4/5 h-full max-w-xs p-6 overflow-y-auto bg-white shadow-lg">
          <button class="absolute text-gray-500 top-4 right-4 hover:text-gray-800" @click="closeMobileMenu"
            aria-label="Tutup menu">
            <HugeiconsIcon :icon="Cancel01Icon" :size="24" :stroke-width="1.8" />
          </button>

          <div class="mb-6">
            <LanguageSwitcher />
          </div>

          <nav>
            <ul class="flex flex-col gap-1">
              <li v-for="item in navItems" :key="item.route">
                <Link :href="route(item.route)" @click="closeMobileMenu"
                  class="flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors font-roboto-slab"
                  :class="isActive(item.route)
                    ? 'text-primary bg-primary/10'
                    : 'text-gray-700 hover:bg-gray-100'
                    ">
                  <HugeiconsIcon :icon="item.icon" :size="20" :stroke-width="1.8" class="text-primary shrink-0" />
                  {{ tWithFallback(item.key, item.fallback) }}
                </Link>
              </li>
              <!-- Menu dinamis dari admin -->
              <li v-for="m in dynamicMenus" :key="m.slug">
                <Link :href="`/halaman/${m.slug}`" @click="closeMobileMenu"
                  class="flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors font-roboto-slab text-gray-700 hover:bg-gray-100">
                  <i v-if="m.icon" :class="m.icon" class="text-primary shrink-0"></i>
                  {{ m.label }}
                </Link>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </Teleport>
  </div>
</template>
